import React, { useState, useEffect } from "react";
import { MapPin, Navigation, Shield, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LocationToggle = ({ user, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [locationEnabled, setLocationEnabled] = useState(user?.location_enabled || false);
  const [showExplainer, setShowExplainer] = useState(false);

  useEffect(() => {
    setLocationEnabled(user?.location_enabled || false);
  }, [user?.location_enabled]);

  const requestLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await api.post("/profile/location", {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            location_enabled: true,
          });
          
          setLocationEnabled(true);
          toast.success("Location enabled! You'll see distances to matches.");
          onUpdate?.();
        } catch (error) {
          console.error("Failed to update location:", error);
          toast.error("Failed to save location");
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        setLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Location permission denied. Enable it in your browser settings.");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location unavailable. Please try again.");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out. Please try again.");
            break;
          default:
            toast.error("Failed to get location");
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes
      }
    );
  };

  const disableLocation = async () => {
    setLoading(true);
    try {
      await api.delete("/profile/location");
      setLocationEnabled(false);
      toast.success("Location disabled");
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to disable location");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
            locationEnabled ? "bg-green-100" : "bg-gray-100"
          }`}>
            <MapPin className={`w-5 h-5 ${locationEnabled ? "text-green-600" : "text-gray-400"}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Location Services</h3>
            <p className="text-sm text-gray-500">
              {locationEnabled ? "Enabled - showing approximate distances" : "Optional - see how far matches are"}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowExplainer(!showExplainer)}
          className="text-gray-400 hover:text-gray-600"
        >
          <Shield className="w-5 h-5" />
        </button>
      </div>

      {showExplainer && (
        <div className="bg-blue-50 rounded-lg p-4 mb-4 relative">
          <button
            onClick={() => setShowExplainer(false)}
            className="absolute top-2 right-2 text-blue-400 hover:text-blue-600"
          >
            <X className="w-4 h-4" />
          </button>
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Your Privacy Matters
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• We show <strong>approximate distances</strong> like "Nearby" or "A few miles away"</li>
            <li>• Your <strong>exact location is never shared</strong> with other users</li>
            <li>• You can disable this anytime</li>
            <li>• Location helps find matches in your area</li>
          </ul>
        </div>
      )}

      {locationEnabled ? (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-green-600">
            <Navigation className="w-4 h-4" />
            <span className="text-sm font-medium">Location active</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={disableLocation}
            disabled={loading}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            {loading ? "..." : "Disable"}
          </Button>
        </div>
      ) : (
        <Button
          onClick={requestLocation}
          disabled={loading}
          className="w-full bg-[#1C3A7A] hover:bg-[#15305f]"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="animate-spin">⏳</span> Getting location...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Navigation className="w-4 h-4" />
              Enable Location
            </span>
          )}
        </Button>
      )}
    </div>
  );
};

export default LocationToggle;
